import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";

/**
 * Printable label page — public, no auth required.
 * Loads `/api/public/installed-equipment/{qr_code}` and renders a printable
 * A6-ish label with the QR + minimal identifying info. Auto-triggers window.print().
 */
export default function PrintQrLabel() {
  const { qr } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    fetch(`${backend}/api/public/installed-equipment/${encodeURIComponent(qr)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => setItem(d))
      .catch(() => setError("QR code introuvable"));
  }, [qr]);

  useEffect(() => {
    if (item) {
      const t = setTimeout(() => window.print(), 400);
      return () => clearTimeout(t);
    }
  }, [item]);

  if (error) {
    return (
      <div className="min-h-screen bg-white text-black flex items-center justify-center p-8">
        <div className="text-center">
          <div className="text-lg font-bold mb-2">Erreur</div>
          <div>{error}</div>
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="min-h-screen bg-white text-black flex items-center justify-center p-8">
        Chargement…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-black p-6 print:p-0">
      <style>{`
        @media print {
          @page { size: A6; margin: 6mm; }
          body { background: white !important; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="no-print mb-4 flex items-center gap-2">
        <button onClick={() => window.print()} className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm">Imprimer</button>
        <button onClick={() => window.close()} className="px-3 py-1.5 rounded bg-gray-200 text-sm">Fermer</button>
      </div>

      <div className="border-2 border-black rounded-lg p-4 max-w-md mx-auto" data-testid="qr-label">
        <div className="text-center mb-2">
          <div className="text-[10px] uppercase tracking-widest text-gray-500">BKPRO — Matériel installé</div>
          <div className="text-lg font-bold leading-tight mt-0.5">{item.name}</div>
          {(item.brand || item.model) && (
            <div className="text-sm text-gray-700">{[item.brand, item.model].filter(Boolean).join(" · ")}</div>
          )}
        </div>
        <div className="flex justify-center my-3">
          <QRCodeCanvas value={item.qr_code} size={200} level="H" includeMargin={false} />
        </div>
        <div className="text-center space-y-0.5">
          <div className="font-mono text-sm font-bold tracking-widest">{item.qr_code}</div>
          {item.serial_number && (
            <div className="text-xs text-gray-600">S/N : {item.serial_number}</div>
          )}
          {item.installed_at && (
            <div className="text-[10px] text-gray-500">Installé le {item.installed_at}</div>
          )}
          <div className="text-[9px] text-gray-500 mt-1 uppercase tracking-widest">Scanne ce code pour signaler un SAV</div>
        </div>
      </div>
    </div>
  );
}
